/**
 * AVS ERP — Authoritative 9-Module Master Workspace Registry
 *
 * Consolidates all 295+ system capabilities into 9 logical parent modules:
 * 1. Manufacturing (/workshop) - Sauda, Dhadi, Nave, Bench, Outside, Melt, Vibrator, HUID, Settlement
 * 2. Stock (/stock) - Ready, Barcode, Lots, Transfers, Boxes/Trays, Stones, Physical Audit
 * 3. Sales (/billing) - Retail POS, Invoices, Wholesale, Estimates, Orders, URD Exchange, Challans
 * 4. People / CRM (/people) - Customers, Suppliers, Karigars, Staff/Employees, CRM, KYC
 * 5. Accounts (/ledger) - Vault 995, Daybook/Rojmel, Ledgers, Vouchers, Bank Reconcile, GST, Conversions
 * 6. Payroll (/attendance) - Attendance, Salary Rules, Advances, Monthly Payroll, Karigar Settlement, Passbook
 * 7. Catalog (/catalog) - Items Master, Design Library, Media Showcase, Catalog Builder, Specs
 * 8. Reports (/reports) - Cockpit, Day Book, Sales Register, Stock Valuation, Metal Position, Wastage, Financials
 * 9. Settings (/settings) - Firm Profile, Rates, Printing, WhatsApp, SMTP, Automation, Security, Backup
 */

export interface WorkspaceSubItem {
  id: string;
  title: string;
  to: string;
  search?: Record<string, unknown>;
  params?: Record<string, string>;
  badge?: string;
  description?: string;
  /** Global shortcut id from shortcuts-store (optional). */
  shortcutId?: string;
  /** Linked automation block id from automation-blocks-catalog (optional). */
  automationBlockId?: string;
}

export interface WorkspaceDefinition {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  iconName: string;
  accentColor: string;
  baseRoute: string;
  items: WorkspaceSubItem[];
}

export const WORKSPACES: Record<string, WorkspaceDefinition> = {
  // 1. MANUFACTURING
  workshop: {
    id: "workshop",
    title: "Manufacturing",
    subtitle: "SRM Mature Workflow (Simplified)",
    description: "Sauda, Dhadi lots, Nave issue, Karigar custody, Jama return, Vibrator recovery, Reconciliation & Settlement.",
    iconName: "Hammer",
    accentColor: "text-amber-500 bg-amber-500/10 border-amber-500/20",
    baseRoute: "/workshop",
    items: [
      { id: "sauda", title: "Bullion / Sauda", to: "/utilities/sauda-book", description: "Bullion purchases, rate fixes & vault receipts" },
      { id: "dhadi", title: "Jobs / Dhadi", to: "/workshop/dhadi-groups", description: "Dhadi traveler lots & job cards" },
      { id: "nave", title: "Nave Issue", to: "/workshop/gold-book", search: { mode: "issue" }, badge: "1", shortcutId: "ws_karigar_book", description: "Metal & stone issue to artisans" },
      { id: "custody", title: "Karigar Custody", to: "/workshop/bench-custody", description: "Artisan bench custody & running gold ledger" },
      { id: "jama", title: "Jama Return", to: "/workshop/jama", badge: "2", description: "Finished goods & scrap returns from artisans" },
      { id: "recovery", title: "Recovery / Vibrator", to: "/workshop/vibrator", description: "Vibrator tumbling, dust recovery & loss audit" },
      { id: "reconciliation", title: "Reconciliation", to: "/workshop/reconciliation", description: "Wastage, over-loss & metal reconciliation" },
      { id: "settlement", title: "Settlement", to: "/settlement/new", description: "Wastage reconciliation, majuri & passbook" },
      { id: "qc_huid", title: "QC / HUID", to: "/stock/hallmark", description: "Quality check & 6-character BIS HUID verification" },
    ],
  },

  // 2. STOCK
  stock: {
    id: "stock",
    title: "Stock",
    subtitle: "Inventory & Bullion",
    description: "Ready stock, barcode search, metal lots, stock transfers, and physical audit.",
    iconName: "Package",
    accentColor: "text-blue-500 bg-blue-500/10 border-blue-500/20",
    baseRoute: "/stock",
    items: [
      { id: "ready_stock", title: "Ready Stock", to: "/stock", search: { tab: "ready" }, badge: "1", shortcutId: "bill_ready_stock", description: "Finished jewellery inventory" },
      { id: "stock_entry", title: "Barcode & Tagging", to: "/stock/entry", description: "Rapid barcode generation & thermal tag print" },
      { id: "lots", title: "Raw Lots / Bullion", to: "/stock/lots", description: "Wholesale bullion packets & raw metal stock" },
      { id: "transfers", title: "Stock Transfers", to: "/stock/transfers", description: "Branch-to-branch & counter inventory movement" },
      { id: "boxes", title: "Boxes & Trays", to: "/stock/boxes", description: "Physical showcase trays and safe storage" },
      { id: "stones", title: "Stones & Diamonds", to: "/stock/stones", description: "Loose diamonds, precious stones & carats" },
      { id: "verification", title: "Physical Audit", to: "/stock/verification", description: "Barcode scan count vs system ledger balance" },
    ],
  },

  // 3. SALES
  retail: {
    id: "retail",
    title: "Sales",
    subtitle: "Showroom & POS",
    description: "Retail POS, wholesale, quotations, URD old gold, customer orders, and delivery.",
    iconName: "ShoppingCart",
    accentColor: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
    baseRoute: "/billing",
    items: [
      { id: "new_sale", title: "Retail POS Billing", to: "/billing/new", badge: "1", shortcutId: "bill_new_invoice", description: "Fast retail barcode checkout & tax invoice" },
      { id: "invoices", title: "Invoices Register", to: "/billing", description: "All sales bills, tax invoices & history" },
      { id: "wholesale", title: "Wholesale Billing", to: "/billing/wholesale", description: "B2B wholesale invoices & deliveries" },
      { id: "estimates", title: "Quotations & Estimates", to: "/billing/estimates", description: "Proforma quotes & customer price approvals" },
      { id: "orders", title: "Customer Orders", to: "/orders", description: "Custom jewellery bookings & production status" },
      { id: "urd", title: "Old Gold / URD Exchange", to: "/utilities/urd-purchase", description: "Customer old gold exchange & scrap purchases" },
      { id: "challans", title: "Delivery Challans", to: "/billing/delivery-challans", description: "Consignment & delivery slips" },
      { id: "credit_notes", title: "Credit & Debit Notes", to: "/billing/credit-notes", description: "Sales returns & price adjustments" },
    ],
  },

  // 4. PEOPLE / CRM
  people: {
    id: "people",
    title: "People / CRM",
    subtitle: "Parties & Team",
    description: "Customers, suppliers, karigars, employees, CRM leads, and KYC document vault.",
    iconName: "Users",
    accentColor: "text-teal-500 bg-teal-500/10 border-teal-500/20",
    baseRoute: "/people",
    items: [
      { id: "customers", title: "Customers (Grahak)", to: "/people", search: { tab: "customers" }, description: "Customer directory, running cash & gold balance" },
      { id: "suppliers", title: "Suppliers", to: "/people", search: { tab: "suppliers" }, description: "Bullion & jewellery vendor directory" },
      { id: "karigars", title: "Karigars (Artisans)", to: "/workshop/worker-books", description: "Artisan roster & bench books" },
      { id: "employees", title: "Staff & Employees", to: "/attendance", search: { tab: "employees" }, description: "Employee roster & designations" },
      { id: "crm", title: "CRM & Leads", to: "/crm/leads", description: "Sales inquiries, appointments & follow-ups" },
      { id: "kyc", title: "KYC & Documents", to: "/people", search: { tab: "kyc" }, description: "Aadhaar, PAN & customer document vault" },
      { id: "who_owes", title: "Who Owes", to: "/ledger", description: "Outstanding gold @995 and cash receivables" },
    ],
  },

  // 5. ACCOUNTS
  accounts: {
    id: "accounts",
    title: "Accounts",
    subtitle: "Cash | Gold @995",
    description: "Dual cash and gold ledgers, Daybook / Rojmel, vouchers, and GST registers.",
    iconName: "Scale",
    accentColor: "text-gold bg-gold/10 border-gold/20",
    baseRoute: "/ledger",
    items: [
      { id: "vault_ledger", title: "Gold Vault @995", to: "/ledger", description: "Authoritative 995 bullion balance & history" },
      { id: "cash_book", title: "Daybook (Rojmel)", to: "/treasury/cash-book", description: "Daily cash receipts (Jama) & payments (Nave)" },
      { id: "ledgers", title: "Party Ledgers", to: "/ledger", search: { tab: "ledgers" }, description: "Customer, supplier & expense running ledgers" },
      { id: "vouchers", title: "Payment & Receipt Vouchers", to: "/treasury/vouchers", description: "Cash & bullion transaction slips" },
      { id: "bank_reconcile", title: "Bank Reconciliation", to: "/treasury/bank-reconciliation", description: "Statement matching & bank feeds" },
      { id: "sub_accounts", title: "Account Heads & Groups", to: "/control/accounts", description: "Chart of accounts, heads & groups" },
      { id: "gst_registers", title: "GST Returns & Registers", to: "/reports/gst-returns", description: "GSTR-1, GSTR-3B & ITC-04 statutory returns" },
      { id: "conversions", title: "Metal Conversions", to: "/conversion/index", description: "Old gold & scrap conversions" },
      { id: "expenses", title: "Shop Expenses", to: "/expenses", description: "Petty cash, operating expenses & drawings" },
    ],
  },

  // 6. PAYROLL
  payroll: {
    id: "payroll",
    title: "Payroll",
    subtitle: "Wages & Attendance",
    description: "Daily attendance, salary rules, advances, monthly payroll, and karigar majuri passbook.",
    iconName: "Banknote",
    accentColor: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
    baseRoute: "/attendance",
    items: [
      { id: "attendance", title: "Attendance Register", to: "/attendance", description: "Daily staff attendance & check-ins" },
      { id: "salary_rules", title: "Salary Rules", to: "/attendance", search: { tab: "rules" }, description: "Base wage, HRA, PF & overtime rules" },
      { id: "advances", title: "Advances & Loans", to: "/attendance", search: { tab: "advances" }, description: "Salary advances & recovery schedules" },
      { id: "payroll_calc", title: "Monthly Payroll", to: "/attendance", search: { tab: "payroll" }, description: "Salary slips, deductions & payouts" },
      { id: "karigar_settle", title: "Karigar Majuri Settlement", to: "/settlement/new", description: "Piece-rate artisan settlement" },
      { id: "passbook", title: "Worker Passbook", to: "/attendance", search: { tab: "passbook" }, description: "Running employee ledger & disbursements" },
    ],
  },

  // 7. CATALOG
  catalog: {
    id: "catalog",
    title: "Catalog",
    subtitle: "Design Showcase",
    description: "Product catalog, design library, media gallery, catalog builder, and spec sheets.",
    iconName: "BookOpen",
    accentColor: "text-indigo-500 bg-indigo-500/10 border-indigo-500/20",
    baseRoute: "/catalog",
    items: [
      { id: "items_master", title: "Products & Items", to: "/catalog", description: "Jewellery product master list" },
      { id: "design_lib", title: "Design Library", to: "/catalog/designs", description: "CAD designs, sketches & categories" },
      { id: "media_gallery", title: "Images & Media", to: "/ui/catalog", description: "High-resolution photography & showcase" },
      { id: "builder", title: "Catalog Builder", to: "/catalog", search: { tab: "builder" }, description: "Export branded PDF jewellery collections" },
      { id: "spec_sheets", title: "Templates & Specs", to: "/settings/customization", description: "Custom tags & layout specifications" },
    ],
  },

  // 8. REPORTS
  reports: {
    id: "reports",
    title: "Reports",
    subtitle: "Day done & registers",
    description: "Sales registers, stock valuation, worker wastage, metal position, and financials.",
    iconName: "FileSpreadsheet",
    accentColor: "text-purple-500 bg-purple-500/10 border-purple-500/20",
    baseRoute: "/reports",
    items: [
      { id: "reports_overview", title: "Reports Cockpit", to: "/reports", description: "All business reports & analytics" },
      { id: "day_book", title: "Daily Close (Day Book)", to: "/reports/daily-close", description: "EOD closing & register reconciliation" },
      { id: "sales_register", title: "Sales Register", to: "/reports/sales-register", description: "B2B and B2C GST invoice breakdowns" },
      { id: "stock_valuation", title: "Stock Valuation", to: "/reports/stock-valuation", description: "Inventory valuation & ageing analysis" },
      { id: "metal_position", title: "Metal Position (@995)", to: "/reports/metal-position", description: "Net unhedged gold exposure & balances" },
      { id: "karigar_wastage", title: "Karigar Wastage Audit", to: "/reports/worker", description: "Loss %, dust recovery & karigar efficiency" },
      { id: "financials", title: "Financial Statements", to: "/reports/financial-statements", description: "Trial Balance, P&L, Balance Sheet" },
      { id: "gst_returns", title: "Statutory Returns (GSTR / ITC-04)", to: "/reports/gst-returns", description: "GSTR-1, GSTR-3B & job-worker ITC-04" },
      { id: "ca_pack", title: "CA Export Pack", to: "/reports/ca-pack", description: "Monthly ZIP export of journals & tax" },
    ],
  },

  // 9. SETTINGS
  settings: {
    id: "settings",
    title: "Settings",
    subtitle: "Firm & System",
    description: "Store profile, bullion rates, printing profiles, WhatsApp, automation, and security.",
    iconName: "Settings",
    accentColor: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20",
    baseRoute: "/settings",
    items: [
      { id: "firm_profile", title: "Firm Profile & Branches", to: "/settings", description: "Store name, GSTIN & branch setup" },
      { id: "rates", title: "Live Bullion Rates", to: "/control/rates", description: "24K, 22K, 18K live rate feeds & markup" },
      { id: "printing", title: "Printing Profiles", to: "/control/customization", description: "Thermal label, A4/A5 invoice layouts" },
      { id: "whatsapp", title: "WhatsApp Automation", to: "/settings/whatsapp", description: "Automated billing messages & templates" },
      { id: "smtp", title: "SMTP & Email", to: "/settings/communications", description: "SMTP server & automated mailers" },
      { id: "automation", title: "Automation Engine", to: "/settings/automation", badge: "1", description: "Native rules & gold-industry blocks" },
      { id: "security", title: "Security & RBAC", to: "/settings/security-center", description: "Login logs, staff permissions & audit" },
      { id: "hardware", title: "Hardware & Devices", to: "/hardware", description: "Digital scales, printers & scanners" },
      { id: "backup", title: "Backup & Restore", to: "/settings/support", description: "Encrypted database backup & diagnostics" },
    ],
  },
};

/**
 * Resolves the active workspace from a given pathname.
 */
export function getActiveWorkspace(pathname: string): WorkspaceDefinition | null {
  if (!pathname || pathname === "/app" || pathname === "/") return null;

  // 1. Manufacturing
  if (
    pathname.startsWith("/workshop") ||
    pathname.startsWith("/melt") ||
    pathname.startsWith("/settlement") ||
    pathname.startsWith("/manufacturing") ||
    pathname.startsWith("/refinery") ||
    pathname.startsWith("/mtg")
  ) {
    return WORKSPACES.workshop;
  }

  // 2. Stock
  if (pathname.startsWith("/stock") || pathname.startsWith("/barcode")) {
    return WORKSPACES.stock;
  }

  // 3. Sales
  if (
    pathname.startsWith("/billing") ||
    pathname.startsWith("/orders") ||
    pathname.startsWith("/scheme") ||
    pathname.startsWith("/repair")
  ) {
    return WORKSPACES.retail;
  }

  // 4. People / CRM
  if (pathname.startsWith("/people") || pathname.startsWith("/crm")) {
    return WORKSPACES.people;
  }

  // 5. Accounts
  if (
    pathname.startsWith("/ledger") ||
    pathname.startsWith("/treasury") ||
    pathname.startsWith("/expenses") ||
    pathname.startsWith("/conversion") ||
    pathname.startsWith("/control/accounts") ||
    pathname.startsWith("/utilities")
  ) {
    return WORKSPACES.accounts;
  }

  // 6. Payroll
  if (pathname.startsWith("/attendance")) {
    return WORKSPACES.payroll;
  }

  // 7. Catalog
  if (pathname.startsWith("/catalog") || pathname.startsWith("/ui/catalog")) {
    return WORKSPACES.catalog;
  }

  // 8. Reports
  if (pathname.startsWith("/reports") || pathname.startsWith("/dashboard")) {
    return WORKSPACES.reports;
  }

  // 9. Settings
  if (
    pathname.startsWith("/settings") ||
    pathname.startsWith("/control") ||
    pathname.startsWith("/hardware") ||
    pathname.startsWith("/communications") ||
    pathname.startsWith("/whatsapp") ||
    pathname.startsWith("/branches") ||
    pathname.startsWith("/ai-center") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/customization") ||
    pathname.startsWith("/help")
  ) {
    return WORKSPACES.settings;
  }

  return null;
}
